import React from 'react';

export default function ControllersExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Controllers (Interface Layer)</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Rôle des Controllers</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Point d'entrée des requêtes HTTP</li>
                                <li>Validation des données d'entrée</li>
                                <li>Conversion DTO ⟷ Domaine</li>
                                <li>Gestion des erreurs HTTP</li>
                                <li>Documentation de l'API</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure Type</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`@RestController
@RequestMapping("/api/v1/payments")
@Tag(name = "Payment Management", description = "API pour la gestion des paiements")
@Validated
@Slf4j
public class PaymentController {
    private final PaymentUseCasePort paymentUseCase;
    private final PaymentDtoMapper mapper;

    @Operation(
        summary = "Créer un nouveau paiement",
        description = "Crée un nouveau paiement avec les détails fournis"
    )
    @ApiResponses({
        @ApiResponse(
            responseCode = "201", 
            description = "Paiement créé"
        ),
        @ApiResponse(
            responseCode = "400", 
            description = "Données invalides"
        )
    })
    @PostMapping
    public ResponseEntity<PaymentResponse> createPayment(
        @Valid @RequestBody CreatePaymentRequest request
    ) {
        log.debug("REST request to create payment: {}", request);

        try {
            // Conversion en commande
            CreatePaymentCommand command = 
                mapper.toCommand(request);

            // Exécution du cas d'utilisation
            PaymentResult result = 
                paymentUseCase.createPayment(command);

            // Conversion de la réponse
            PaymentResponse response = 
                mapper.toResponse(result);

            // Retour avec localisation
            URI location = new URI("/api/v1/payments/" + 
                response.getId());

            return ResponseEntity
                .created(location)
                .body(response);

        } catch (Exception e) {
            log.error("Error creating payment", e);
            throw new PaymentCreationException(
                "Failed to create payment", e);
        }
    }

    @Operation(
        summary = "Obtenir un paiement",
        description = "Récupère les détails d'un paiement par son ID"
    )
    @GetMapping("/{id}")
    public ResponseEntity<PaymentResponse> getPayment(
        @PathVariable UUID id
    ) {
        log.debug("REST request to get payment: {}", id);

        return paymentUseCase
            .getPayment(id)
            .map(mapper::toResponse)
            .map(ResponseEntity::ok)
            .orElseThrow(() -> 
                new PaymentNotFoundException(id));
    }

    @Operation(
        summary = "Traiter un paiement",
        description = "Lance le traitement d'un paiement existant"
    )
    @PostMapping("/{id}/process")
    public ResponseEntity<ProcessingResponse> processPayment(
        @PathVariable UUID id,
        @Valid @RequestBody ProcessPaymentRequest request
    ) {
        log.debug("REST request to process payment: {}", id);

        ProcessPaymentCommand command = ProcessPaymentCommand
            .builder()
            .paymentId(id)
            .metadata(request.getMetadata())
            .build();

        ProcessingResult result = 
            paymentUseCase.processPayment(command);

        return ResponseEntity.ok(
            mapper.toProcessingResponse(result)
        );
    }
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples de Controllers</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">SWIFT Message Controller</h4>
                                <pre className="text-xs mt-2">
{`@RestController
@RequestMapping("/api/v1/swift/messages")
@Tag(name = "SWIFT Messages")
@Slf4j
public class SwiftMessageController {
    private final MessageUseCasePort messageUseCase;
    private final SwiftMessageMapper mapper;

    @PostMapping("/mt103")
    public ResponseEntity<MT103Response> createMT103(
        @Valid @RequestBody MT103Request request,
        @RequestHeader HttpHeaders headers
    ) {
        log.info("Creating MT103 message");
        validateHeaders(headers);

        CreateMessageCommand command = 
            mapper.toCommand(request);

        MessageResult result = 
            messageUseCase.createMessage(command);

        return ResponseEntity.ok(
            mapper.toMT103Response(result)
        );
    }

    @GetMapping("/status/{messageId}")
    public ResponseEntity<MessageStatusResponse> getStatus(
        @PathVariable String messageId
    ) {
        return messageUseCase
            .getMessageStatus(messageId)
            .map(mapper::toStatusResponse)
            .map(ResponseEntity::ok)
            .orElseThrow(() -> 
                new MessageNotFoundException(messageId));
    }

    private void validateHeaders(HttpHeaders headers) {
        // Validation des en-têtes spécifiques SWIFT
    }
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Mandate Controller</h4>
                                <pre className="text-xs mt-2">
{`@RestController
@RequestMapping("/api/v1/mandates")
@Tag(name = "Mandate Management")
@Slf4j
public class MandateController {
    private final MandateUseCasePort mandateUseCase;
    private final MandateDtoMapper mapper;

    @PostMapping
    @PreAuthorize("hasRole('MANDATE_CREATE')")
    public ResponseEntity<MandateResponse> createMandate(
        @Valid @RequestBody CreateMandateRequest request
    ) {
        CreateMandateCommand command = 
            mapper.toCommand(request);

        MandateResult result = 
            mandateUseCase.createMandate(command);

        return ResponseEntity
            .status(HttpStatus.CREATED)
            .body(mapper.toResponse(result));
    }

    @PutMapping("/{id}/amend")
    @PreAuthorize("hasRole('MANDATE_AMEND')")
    public ResponseEntity<MandateResponse> amendMandate(
        @PathVariable UUID id,
        @Valid @RequestBody AmendMandateRequest request
    ) {
        AmendMandateCommand command = AmendMandateCommand
            .builder()
            .mandateId(id)
            .amendments(request.getAmendments())
            .reason(request.getReason())
            .build();

        MandateResult result = 
            mandateUseCase.amendMandate(command);

        return ResponseEntity.ok(
            mapper.toResponse(result)
        );
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Patterns d'Implémentation</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Global Error Handling</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@RestControllerAdvice
@Slf4j
public class GlobalExceptionHandler {
    
    @ExceptionHandler(ValidationException.class)
    @ResponseStatus(HttpStatus.BAD_REQUEST)
    public ErrorResponse handleValidation(
        ValidationException ex
    ) {
        log.warn("Validation error", ex);
        return ErrorResponse.builder()
            .code("VALIDATION_ERROR")
            .message(ex.getMessage())
            .errors(ex.getErrors())
            .build();
    }

    @ExceptionHandler(BusinessException.class)
    @ResponseStatus(HttpStatus.UNPROCESSABLE_ENTITY)
    public ErrorResponse handleBusiness(
        BusinessException ex
    ) {
        log.error("Business error", ex);
        return ErrorResponse.builder()
            .code("BUSINESS_ERROR")
            .message(ex.getMessage())
            .build();
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Request Validation</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@RestController
public class ValidatedController {
    
    @PostMapping("/validate")
    public ResponseEntity<Result> validateRequest(
        @Valid @RequestBody ValidatedRequest request,
        @RequestHeader("X-Correlation-ID") String correlationId,
        BindingResult bindingResult
    ) {
        if (bindingResult.hasErrors()) {
            List<String> errors = bindingResult
                .getFieldErrors()
                .stream()
                .map(e -> e.getField() + ": " + 
                     e.getDefaultMessage())
                .collect(Collectors.toList());
                
            return ResponseEntity
                .badRequest()
                .body(Result.failure(errors));
        }

        // Traitement normal si validation OK
        return ResponseEntity.ok(
            Result.success(processRequest(request))
        );
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Bonnes Pratiques</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Validation</h4>
                            <p className="text-sm text-gray-600">
                                Validation complète des entrées
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                @Valid, Custom Validators
                            </code>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Documentation</h4>
                            <p className="text-sm text-gray-600">
                                Documentation claire et complète de l'API
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                OpenAPI, Swagger
                            </code>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Versioning</h4>
                            <p className="text-sm text-gray-600">
                                Gestion du versioning de l'API
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                URI versioning, Headers
                            </code>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Security</h4>
                            <p className="text-sm text-gray-600">
                                Sécurisation des endpoints
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                @Secured, @PreAuthorize
                            </code>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Error Handling</h4>
                            <p className="text-sm text-gray-600">
                                Gestion cohérente des erreurs
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                @ExceptionHandler, @RestControllerAdvice
                            </code>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}